import { Autocomplete, TextField, createFilterOptions } from "@mui/material";

import { Controller } from "react-hook-form";
import React from "react";

interface IFormInput {
    id?: string;
    name: string;
    label: string;
    control: any;
    error: any;
    disabled?: boolean;
    readOnly?: any;
    startOnFocus?: boolean;
    startValue?: any;
    options: any;
    onChange?: (value: string | number | {}) => void;
    concatWithID?: boolean;
    onInputChange?: any;
    placeholder?: string;
}

const textField: React.FC<IFormInput> = ({
    id,
    name,
    label,
    options,
    control,
    error,
    readOnly,
    disabled,
    startValue,
    startOnFocus,
    onChange,
    concatWithID: concat,
    onInputChange,
    placeholder,
}) => {
    // Garante sempre um array — se o backend falhar (cold start / 500), o estado
    // pode chegar como undefined e o Autocomplete quebraria ao ler `.length`.
    const safeOptions: any[] = Array.isArray(options) ? options : [];

    // Null-safe: registros incompletos (label/value nulos) não podem lançar
    // exceção, senão a tela fica branca ou o filtro devolve a lista inteira.
    const getOptionLabel = (option: any) => {
        if (!option || typeof option !== "object") return "";
        const optValue = option.value != null ? String(option.value) : "";
        const optLabel = option.label != null ? String(option.label) : "";
        if (optValue.toLowerCase() === optLabel.toLowerCase()) {
            return optLabel;
        }
        if (concat) {
            return optLabel ? `${option.value} - ${optLabel}` : "";
        }
        return optLabel;
    };

    // Filtro explícito e null-safe — corrige opções sem relação aparecendo na busca.
    const filterOptions = createFilterOptions({
        stringify: (option: any) => {
            const optLabel = option?.label != null ? String(option.label) : "";
            const optValue = option?.value != null ? String(option.value) : "";
            return concat ? `${optValue} ${optLabel}` : optLabel;
        },
    });

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onChange: renderChange, value } }) => (
                <Autocomplete
                    id={id}
                    clearText="Limpar"
                    openText="Abrir"
                    closeText="Fechar"
                    noOptionsText="Nenhum registro"
                    disabled={disabled}
                    options={safeOptions}
                    readOnly={readOnly}
                    filterOptions={filterOptions}
                    isOptionEqualToValue={(option: any, selected: any) =>
                        String(option?.value) === String(selected?.value)
                    }
                    value={
                        safeOptions.find((item: { value: any }) => {
                            return item?.value === value || item?.value === startValue;
                        }) || null
                    }
                    getOptionLabel={getOptionLabel}
                    renderOption={(optionProps, option: any) => (
                        // Key única pelo `value` (id). Sem isto, o MUI usa o label como
                        // key; rótulos repetidos (ex.: duas concessionárias com o mesmo
                        // nome) geram keys duplicadas e o React não remove os <li>
                        // antigos ao filtrar — sobram opções fantasma sem relação.
                        <li {...optionProps} key={`${option?.value}-${getOptionLabel(option)}`}>
                            {getOptionLabel(option)}
                        </li>
                    )}
                    onChange={(_, item) => {
                        renderChange(item?.value || "");
                        if (typeof onChange === "function")
                            onChange(item?.tipo ? { ...item } : item?.value ? item.value : "");
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            autoFocus={startOnFocus}
                            size="small"
                            margin="normal"
                            label={label}
                            error={!!error && !!error[name]}
                            helperText={!!error && error[name]?.message}
                            fullWidth
                            placeholder={placeholder}
                        />
                    )}
                    onInputChange={onInputChange}
                />
            )}
        />
    );
};

export default textField;
